import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useAtom } from 'jotai'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { colors } from '../../../../theme/palette'
import { baseButton } from '../../../../theme/reuseables'
import { profileAtom } from '../../../../api/profile/profile'
import { L } from '../../../providers/Lokalise'
import { ImitatorNoAccessWarning } from '../../../modules/Imitator/ImitatorNoAccessWarning'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import {
    FormSelect,
    FormTextArea,
} from '../../../modules/FormElements/FormInput'
import { CaseDetail } from '../../../../models/CaseDetail'
import {
    createReminder,
    CreateReminderType,
    getReminderReasonList,
} from '../../../../api/salary'
import { FormMessage } from '../../../ui/FormElememts/FormMessage'
import Checkbox from '../../../ui/FormElememts/Checkbox'
import { Select } from '../../../ui/FormElememts/Select'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { ErrorDisplay } from '../Contact/ContactDialog'
import { DatePickerInput } from '../../../ui/FormElememts/DatePickerInput'

interface EditProps {
    onUpdated?: () => void
    onClickCancel?: () => void
    caseDetail?: CaseDetail
    isOpen?: boolean
}

const useStyles = createUseStyles({
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    accordionWrapper: {
        marginBottom: '20px',
    },
    select: {
        position: 'relative',
        height: '3.125rem',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
})

export type RegistrationFormFields = {
    reminderAarsagFritekst: string
    reminderAarsagNr: string
    sagsRegTid: string
    sagsId: string
    sendReminderEmail: boolean
    reminderDato: string
}

type CustomErrors = {
    reminderDato?: boolean
    reminderAarsagFritekst?: boolean
    reminderAarsagNr?: boolean
}

export const CreateReminderView: FC<EditProps> = ({
    caseDetail,
    onUpdated,
    onClickCancel,
    isOpen = false,
}) => {
    const [authProfile] = useAtom(profileAtom)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const {
        register,
        handleSubmit,
        formState,
        reset,
        setValue,
        getValues,
        watch,
    } = useForm<RegistrationFormFields>({
        defaultValues: {
            sendReminderEmail: true,
        },
    })
    const classes = useStyles()
    const [, setIsValid] = useState(false)
    const [reminderReasonList, setReminderReasonList] = useState<any[]>([])
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [selectedReminderReason, setSelectedReminderReason] = useState<
        any | undefined
    >(undefined)
    const [customErrors, setCustomErrors] = useState<CustomErrors>({})
    const [isLoading, setIsLoading] = useState(false)
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const [reminderDateError, setReminderDateError] = useState<{
        hasError: boolean
        message: string
        show: boolean
    }>({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const resetFormState = (): void => {
        setSelectedDate(null)
        setSelectedReminderReason(undefined)
        setCustomErrors({})
        setIsValid(false)
        setIsLoading(false)
    }

    const resetForm = (): void => {
        reset()
        resetFormState()
    }

    useEffect(() => {
        const fetchReminderReasonList = async (): Promise<void> => {
            const response = await getReminderReasonList()
            setReminderReasonList(response)
        }
        fetchReminderReasonList()
    }, [])

    useEffect(() => {
        if (isOpen) {
            reset({
                reminderAarsagFritekst: '',
                reminderAarsagNr: '',
                reminderDato: '',
                sendReminderEmail: true,
            })
            resetFormState()
        }
    }, [isOpen, reset])

    const { isDirty } = formState

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        if (reminderDateError.hasError) {
            setReminderDateError((prev) => ({
                ...prev,
                show: true,
            }))
            return
        }
        let hasErrors = false
        if (authProfile.isImitator) {
            setShowImitatorModal(true)
            return
        }

        const errors = {
            reminderAarsagFritekst: false,
            reminderAarsagNr: false,
            reminderDato: false,
        }

        if (
            selectedReminderReason?.kraeverFritekst &&
            !data.reminderAarsagFritekst
        ) {
            errors.reminderAarsagFritekst = true
            hasErrors = true
        }

        if (!selectedDate) {
            errors.reminderDato = true
            hasErrors = true
        }

        if (!selectedReminderReason) {
            errors.reminderAarsagNr = true
            hasErrors = true
        }

        if (hasErrors) {
            setIsLoading(false)
            setCustomErrors(errors)
            return
        }

        const fieldsToEdit: CreateReminderType = {
            caseId: caseDetail.caseId,
            caseRegTime: caseDetail.caseRegistrationTime,
            reminderDate: moment(selectedDate).format('YYYY-MM-DD'),
            reminderReason: +selectedReminderReason.nr,
            reminderFreeText: data.reminderAarsagFritekst,
            sendReminderEmail: data.sendReminderEmail,
        }

        if (!hasErrors) {
            setIsLoading(true)
            const response = await createReminder(fieldsToEdit)
            if (response.error) {
                showError(<ErrorDisplay errors={response.error.errors} />)
                setIsLoading(false)
                return
            }
            if (onUpdated && response) {
                onUpdated()
            }
            setIsLoading(false)
        }
    }

    const checkIsValid = (): void => {
        if (isDirty && selectedDate && !!getValues('reminderAarsagNr')) {
            setIsValid(true)
        } else {
            setIsValid(false)
        }
    }

    useEffect(() => {
        checkIsValid()
    })

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div
                className={`${classes.contentWrapper} ${
                    isOpen ? 'visible' : ''
                }`}
            >
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    {/* Opret reminder */}
                    {L('LØN_REMINDERS_OPRET_FORM_TITEL')}
                </Typography>

                <FormControl>
                    <DatePickerInput
                        label={`${L(
                            'LØN_REMINDERS_OPRET_FORM_REMINDER_VÆLG_DATO_LABEL'
                        )} *`}
                        selected={selectedDate}
                        setSelected={(e) => {
                            setCustomErrors({})
                            setSelectedDate(e)
                        }}
                        minDate={moment().add(1, 'day').startOf('day').toDate()}
                        maxDate={moment().add(1, 'year').toDate()}
                        hasError={!!customErrors?.reminderDato}
                        onChangeDateInputInvalid={(isValid, message) => {
                            setReminderDateError({
                                hasError: !isValid,
                                message: message ?? 'Datoen er ikke gyldig',
                                show: reminderDateError.show && !isValid, // Only set to false if currently true and input is valid
                            })
                        }}
                    />
                    <FormMessage
                        text=""
                        error={
                            customErrors.reminderDato
                                ? 'Reminder-dato påkrævet'
                                : undefined
                        }
                    />
                    {reminderDateError.hasError && reminderDateError.show && (
                        <FormMessage
                            text={reminderDateError.message}
                            error={reminderDateError.message}
                        />
                    )}
                </FormControl>

                <FormControl>
                    {/* Vælg årsag */}
                    <Select
                        name="reminderAarsagNr"
                        options={reminderReasonList?.map((item) => ({
                            label: item.tekst,
                            value: item.nr,
                        }))}
                        placeholder={`${L(
                            'LØN_REMINDERS_OPRET_FORM_VÆLG_ÅRSAG_LABEL'
                        )} *`}
                        required
                        onChange={(e) => {
                            setCustomErrors({})
                            const found = reminderReasonList.find(
                                (item) => +item.nr === +e.target.value
                            )
                            if (found) {
                                setSelectedReminderReason(found)
                                if (customErrors.reminderAarsagFritekst) {
                                    setCustomErrors({
                                        ...customErrors,
                                        reminderAarsagFritekst: false,
                                    })
                                }
                            }
                        }}
                        hasError={customErrors.reminderAarsagNr}
                    />
                    <FormMessage
                        text=""
                        error={
                            customErrors.reminderAarsagNr
                                ? 'Årsag påkrævet'
                                : undefined
                        }
                    />
                </FormControl>

                <FormControl>
                    {/* Skriv evt. tekst */}
                    <FormTextArea
                        label={`${L(
                            'LØN_REMINDERS_OPRET_FORM_SKRIV_TEKST_LABEL'
                        )} ${
                            selectedReminderReason?.kraeverFritekst ? '*' : ''
                        }`}
                        register={register}
                        name="reminderAarsagFritekst"
                        maxCount={900}
                        hasError={customErrors.reminderAarsagFritekst}
                    />
                    <FormMessage
                        text=""
                        error={
                            customErrors.reminderAarsagFritekst
                                ? 'Tekst påkrævet'
                                : undefined
                        }
                    />
                </FormControl>

                <FormControl>
                    <Checkbox
                        label={L('LØN_REMINDERS_OPRET_FORM_SEND_EMAIL_LABEL')}
                        onChange={(e) => {
                            setValue('sendReminderEmail', e.target.checked, {
                                shouldDirty: true,
                            })
                        }}
                        isChecked={watch('sendReminderEmail')}
                    />
                </FormControl>
                <div />

                <ImitatorNoAccessWarning
                    show={showImitatorModal}
                    onClose={() => setShowImitatorModal(false)}
                />
            </div>
            <div className={classes.bottom}>
                <Button
                    onClick={() => {
                        resetForm()
                        onClickCancel()
                    }}
                    type="secondary"
                    size="large"
                >
                    {L('LØN_REMINDERS_OPRET_FORM_ANNULLER_KNAP')}
                </Button>
                <Button
                    buttonType="submit"
                    type="primary"
                    size="large"
                    disabled={isLoading}
                    loading={isLoading}
                >
                    {L('LØN_REMINDERS_OPRET_FORM_OPRET_KNAP')}
                </Button>
            </div>
            <ErrorDialogComponent />
        </form>
    )
}
